import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ArrowLeft,
  Trash2,
  KeyRound,
  UserPlus,
  ToggleLeft,
  ToggleRight,
  Building2,
  UserCog,
  Mail,
  AlertCircle,
  Upload,
  CheckSquare,
  Square,
  X,
  CalendarDays,
} from "lucide-react";
import Header from "../../components/Header";
import LoadingLogo from "../../components/LoadingLogo";
import BatchUserImport from "../../components/admin/BatchUserImport";
import { AuthContext } from "../../contexts/AuthContext";
import {
  getAuthenticatedAxios,
  buildApiPath,
  API_ENDPOINTS,
} from "../../utils/api";

function UserManagement() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Selection state for batch actions
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);

  // Load users and companies
  const loadData = useCallback(async (page = currentPage, itemsPerPage = perPage) => {
    try {
      setError(null);
      setLoading(true);
      const axios = getAuthenticatedAxios();

      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: itemsPerPage.toString(),
      });

      // Add filters if they exist
      if (searchTerm) params.append("search", searchTerm);
      if (filterCompany) params.append("company_id", filterCompany);
      if (filterRole) params.append("role", filterRole);
      if (filterStatus)
        params.append(
          "is_active",
          filterStatus === "active" ? "true" : "false",
        );

      // Load users and companies in parallel
      const [usersResponse, companiesResponse] = await Promise.all([
        axios.get(`${buildApiPath(API_ENDPOINTS.ADMIN.USERS)}?${params}`),
        axios.get(buildApiPath(API_ENDPOINTS.ADMIN.COMPANIES)),
      ]);

      // Handle paginated response from backend
      // Backend returns { items: [], meta: {} } structure
      const usersData = usersResponse.data.items || [];
      const companiesData = companiesResponse.data.items || [];
      const meta = usersResponse.data.meta || {};

      setUsers(Array.isArray(usersData) ? usersData : []);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);

      // Update pagination state
      setTotalUsers(meta.total || 0);
      setTotalPages(meta.total_pages || 0);
      setCurrentPage(meta.page || page);
      setPerPage(meta.per_page || itemsPerPage);
    } catch (err) {
      setError("Failed to load users and companies");
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterCompany, filterRole, filterStatus, perPage, searchTerm]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterCompany, filterRole, filterStatus, searchTerm]);

  // Single source of truth for loading data
  useEffect(() => {
    loadData(currentPage, perPage);
  }, [currentPage, perPage, loadData]);

  // No need for client-side filtering anymore since we're using server-side filtering
  const filteredUsers = users;

  // Handle user actions
  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const axios = getAuthenticatedAxios();
      await axios.put(`${buildApiPath(API_ENDPOINTS.ADMIN.USERS)}/${userId}`, {
        is_active: !currentStatus,
      });

      setSuccessMessage(
        `User ${!currentStatus ? "activated" : "deactivated"} successfully`,
      );
      await loadData();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(
        `Failed to update user status: ${err.response?.data?.detail || err.message}`,
      );
    }
  };

  const handleResetPassword = async (userId, userEmail) => {
    if (!window.confirm(`Reset password for ${userEmail}?`)) return;

    try {
      const axios = getAuthenticatedAxios();
      const response = await axios.post(
        `${buildApiPath(API_ENDPOINTS.ADMIN.USERS)}/${userId}/reset-password`,
      );

      // Show temporary password to admin
      alert(
        `Temporary password for ${userEmail}:\n\n${response.data.temporary_password}\n\nPlease share this securely with the user.`,
      );

      setSuccessMessage("Password reset successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(
        `Failed to reset password: ${err.response?.data?.detail || err.message}`,
      );
    }
  };

  const handleResendInvitation = async (userId, userEmail) => {
    if (!window.confirm(`Resend invitation email to ${userEmail}?`)) return;

    try {
      const axios = getAuthenticatedAxios();
      await axios.post(
        `${buildApiPath(API_ENDPOINTS.ADMIN.USERS)}/${userId}/resend-invitation`,
        { language: "en" },
      );

      setSuccessMessage(`Invitation email resent to ${userEmail}`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(
        `Failed to resend invitation: ${err.response?.data?.detail || err.message}`,
      );
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const axios = getAuthenticatedAxios();
      await axios.put(
        `${buildApiPath(API_ENDPOINTS.ADMIN.USERS)}/${userId}`,
        updates,
      );

      setSuccessMessage("User updated successfully");
      setShowEditModal(false);
      setEditingUser(null);
      await loadData();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(
        `Failed to update user: ${err.response?.data?.detail || err.message}`,
      );
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${userEmail}? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      setError(null); // Clear any previous errors
      const axios = getAuthenticatedAxios();
      const deleteUrl = `${buildApiPath(API_ENDPOINTS.ADMIN.USERS)}/${userId}`;

      await axios.delete(deleteUrl);

      setSuccessMessage(`User ${userEmail} deleted successfully`);

      // Reload the users list
      await loadData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || err.message || "Unknown error occurred";
      setError(`Failed to delete user: ${errorMessage}`);

      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    }
  };

  // Selection handlers
  const handleSelectUser = (userId) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      // Select all users except current user
      setSelectedUserIds(
        users
          .filter((u) => {
            const userId = u.id || u.user_id || u.UserId;
            return userId !== user?.id;
          })
          .map((u) => u.id || u.user_id || u.UserId),
      );
    }
  };

  const handleBatchDelete = async () => {
    if (selectedUserIds.length === 0) return;

    setBatchDeleting(true);
    setError(null);

    try {
      const axios = getAuthenticatedAxios();
      const response = await axios.post(
        `${buildApiPath(API_ENDPOINTS.ADMIN.USERS)}/batch-delete`,
        selectedUserIds,
      );

      const { successful, failed, results } = response.data;

      if (successful > 0) {
        setSuccessMessage(`Successfully deleted ${successful} user(s)`);
        setSelectedUserIds([]);
        await loadData();
      }

      if (failed > 0) {
        const failedUsers = results.filter((r) => !r.success);
        const errorMessages = failedUsers
          .map((r) => `${r.user_id}: ${r.message}`)
          .join(", ");
        setError(`Failed to delete ${failed} user(s): ${errorMessages}`);
      }

      setShowBatchDeleteConfirm(false);

      // Clear messages after 5 seconds
      setTimeout(() => {
        setSuccessMessage("");
        setError(null);
      }, 5000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || err.message || "Unknown error occurred";
      setError(`Failed to batch delete users: ${errorMessage}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setBatchDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              User Management
            </h1>
            <p className="text-gray-600 mt-1">
              Manage all system users across companies
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/admin/user-activity")}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
            >
              <CalendarDays className="h-4 w-4" />
              Activity Timeline
            </button>
            <button
              onClick={() => navigate("/admin/users/invite")}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <UserPlus className="h-4 w-4" />
              Invite User
            </button>
            <button
              onClick={() => navigate("/admin/users/new")}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <UserPlus className="h-4 w-4" />
              Add Admin/Demo User
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Batch Import
            </button>
            <button
              onClick={() => navigate("/admin")}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {successMessage}
          </div>
        )}

        {/* Batch Actions Bar */}
        {selectedUserIds.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex justify-between items-center">
            <span className="text-blue-700">
              {selectedUserIds.length} user(s) selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedUserIds([])}
                className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Clear Selection
              </button>
              <button
                onClick={() => setShowBatchDeleteConfirm(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete Selected
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Company Filter */}
            <div>
              <select
                value={filterCompany}
                onChange={(e) => setFilterCompany(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Companies</option>
                {companies.map((company, index) => {
                  const companyId = company.id || company.CompanyId;
                  const companyName = company.name || company.Name;
                  const uniqueKey = companyId
                    ? `${companyId}-${index}`
                    : `company-${index}`;

                  return (
                    <option key={uniqueKey} value={companyId}>
                      {companyName}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Role Filter */}
            <div>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <LoadingLogo size="md" message="Loading users..." />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No users found matching your filters
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={handleSelectAll}
                        className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                      >
                        {selectedUserIds.length ===
                        users.filter((u) => {
                          const userId = u.id || u.user_id || u.UserId;
                          return userId !== user?.id;
                        }).length ? (
                          <CheckSquare className="h-4 w-4" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                        Select All
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((userItem, index) => {
                    // Handle both snake_case and PascalCase field names
                    const userId =
                      userItem.id || userItem.user_id || userItem.UserId;
                    const email = userItem.email || userItem.Email;
                    const fullName = userItem.full_name || userItem.FullName;
                    const companyName =
                      userItem.company_name || userItem.CompanyName || "N/A";
                    const role = userItem.role || userItem.Role;
                    const isActive =
                      userItem.is_active !== undefined
                        ? userItem.is_active
                        : userItem.IsActive;
                    const createdAt = userItem.created_at || userItem.CreatedAt;
                    // For backward compatibility: if HasSetPassword is not set, assume true
                    // This ensures all users are visible
                    const hasSetPassword =
                      userItem.has_set_password !== undefined
                        ? userItem.has_set_password
                        : userItem.HasSetPassword !== undefined
                          ? userItem.HasSetPassword
                          : true; // Default to true for users created before this feature

                    // Use a combination of userId and index to ensure unique keys
                    const uniqueKey = userId
                      ? `${userId}-${index}`
                      : `user-${index}`;

                    const isCurrentUser = userId === user?.id;

                    return (
                      <tr key={uniqueKey} className="hover:bg-gray-50">
                        <td className="px-6 py-3 whitespace-nowrap">
                          <button
                            onClick={() =>
                              !isCurrentUser && handleSelectUser(userId)
                            }
                            disabled={isCurrentUser}
                            className={`p-1 ${isCurrentUser ? "opacity-50 cursor-not-allowed" : "hover:text-blue-600"}`}
                            title={
                              isCurrentUser
                                ? "Cannot select yourself"
                                : "Select user"
                            }
                          >
                            {selectedUserIds.includes(userId) ? (
                              <CheckSquare className="h-5 w-5" />
                            ) : (
                              <Square className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {fullName}
                              </span>
                              {!hasSetPassword && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  Invitation Pending
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Building2 className="h-4 w-4 mr-2 text-gray-400" />
                            {companyName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              role === "admin"
                                ? "bg-purple-100 text-purple-800"
                                : role === "demo"
                                  ? "bg-amber-100 text-amber-800"
                                  : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              isActive
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEditUser(userItem)}
                              className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit User"
                            >
                              <UserCog className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleToggleStatus(userId, isActive)
                              }
                              className={`p-1.5 rounded-lg transition-colors ${
                                isActive
                                  ? "text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                                  : "text-green-600 hover:text-green-800 hover:bg-green-50"
                              }`}
                              title={
                                isActive ? "Deactivate User" : "Activate User"
                              }
                            >
                              {isActive ? (
                                <ToggleRight className="h-4 w-4" />
                              ) : (
                                <ToggleLeft className="h-4 w-4" />
                              )}
                            </button>
                            {!hasSetPassword ? (
                              <button
                                onClick={() =>
                                  handleResendInvitation(userId, email)
                                }
                                className="p-1.5 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded-lg transition-colors"
                                title="Resend Invitation"
                              >
                                <Mail className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() =>
                                  handleResetPassword(userId, email)
                                }
                                className="p-1.5 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                                title="Reset Password"
                              >
                                <KeyRound className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                // Check both possible field names for current user
                                const currentUserId = user?.id || user?.UserId;
                                const isSelfDelete =
                                  currentUserId &&
                                  (userId === currentUserId ||
                                    userId === user?.id);

                                if (isSelfDelete) {
                                  setError(
                                    "You cannot delete your own account",
                                  );
                                  setTimeout(() => setError(null), 3000);
                                  return;
                                }
                                handleDeleteUser(userId, email);
                              }}
                              className={`p-1.5 rounded-lg transition-colors ${
                                user &&
                                (userId === user.id || userId === user.UserId)
                                  ? "text-gray-400 cursor-not-allowed"
                                  : "text-red-600 hover:text-red-800 hover:bg-red-50"
                              }`}
                              title={
                                user &&
                                (userId === user.id || userId === user.UserId)
                                  ? "Cannot delete your own account"
                                  : "Delete User"
                              }
                              disabled={false}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination Controls */}
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  {/* Results info */}
                  <div className="text-sm text-gray-700">
                    Showing{" "}
                    <span className="font-medium">
                      {Math.min((currentPage - 1) * perPage + 1, totalUsers)}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(currentPage * perPage, totalUsers)}
                    </span>{" "}
                    of <span className="font-medium">{totalUsers}</span> users
                  </div>

                  {/* Page controls */}
                  <div className="flex items-center space-x-2">
                    {/* Per page selector */}
                    <select
                      value={perPage}
                      onChange={(e) => {
                        setPerPage(Number(e.target.value));
                        setCurrentPage(1); // Reset to first page when changing items per page
                      }}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                      <option value={100}>100 per page</option>
                    </select>

                    {/* Previous button */}
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className={`px-3 py-1 border rounded-md text-sm ${
                        currentPage <= 1
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Previous
                    </button>

                    {/* Page numbers */}
                    <div className="flex items-center space-x-1">
                      {/* Show page numbers intelligently */}
                      {totalPages <= 7 ? (
                        // Show all pages if 7 or less
                        Array.from({ length: totalPages }, (_, i) => i + 1).map(
                          (pageNum) => (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-3 py-1 border rounded-md text-sm ${
                                pageNum === currentPage
                                  ? "bg-blue-600 text-white"
                                  : "bg-white text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {pageNum}
                            </button>
                          ),
                        )
                      ) : (
                        // Show smart pagination for many pages
                        <>
                          {currentPage > 2 && (
                            <>
                              <button
                                onClick={() => setCurrentPage(1)}
                                className="px-3 py-1 border rounded-md text-sm bg-white text-gray-700 hover:bg-gray-50"
                              >
                                1
                              </button>
                              {currentPage > 3 && (
                                <span className="px-2">...</span>
                              )}
                            </>
                          )}

                          {/* Current page and neighbors */}
                          {[currentPage - 1, currentPage, currentPage + 1]
                            .filter(
                              (pageNum) => pageNum > 0 && pageNum <= totalPages,
                            )
                            .map((pageNum) => (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`px-3 py-1 border rounded-md text-sm ${
                                  pageNum === currentPage
                                    ? "bg-blue-600 text-white"
                                    : "bg-white text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                {pageNum}
                              </button>
                            ))}

                          {currentPage < totalPages - 1 && (
                            <>
                              {currentPage < totalPages - 2 && (
                                <span className="px-2">...</span>
                              )}
                              <button
                                onClick={() => setCurrentPage(totalPages)}
                                className="px-3 py-1 border rounded-md text-sm bg-white text-gray-700 hover:bg-gray-50"
                              >
                                {totalPages}
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>

                    {/* Next button */}
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className={`px-3 py-1 border rounded-md text-sm ${
                        currentPage >= totalPages
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Edit User
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const updates = {
                  full_name: formData.get("full_name"),
                  role: formData.get("role"),
                  is_active: formData.get("is_active") === "true",
                  company_id: formData.get("company_id"),
                };
                const userId = editingUser.id || editingUser.UserId;
                handleUpdateUser(userId, updates);
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    defaultValue={editingUser.full_name || editingUser.FullName}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editingUser.email || editingUser.Email}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-100 sm:text-sm"
                    disabled
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <select
                    name="role"
                    defaultValue={editingUser.role || editingUser.Role}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="user">Regular User</option>
                    <option value="admin">Admin</option>
                    <option value="demo">Demo User</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Company
                  </label>
                  <select
                    name="company_id"
                    defaultValue={
                      editingUser.company_id || editingUser.CompanyId
                    }
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {companies.map((company, index) => {
                      const companyId = company.id || company.CompanyId;
                      const companyName = company.name || company.Name;
                      return (
                        <option key={companyId || index} value={companyId}>
                          {companyName}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    name="is_active"
                    defaultValue={(editingUser.is_active !== undefined
                      ? editingUser.is_active
                      : editingUser.IsActive
                    ).toString()}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Batch Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Batch Import Users
              </h3>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <BatchUserImport
              onImportComplete={(results) => {
                setSuccessMessage(
                  `Import complete: ${results.successful} users imported successfully`,
                );
                setShowImportModal(false);
                loadData(); // Reload the user list
                setTimeout(() => setSuccessMessage(""), 5000);
              }}
            />
          </div>
        </div>
      )}

      {/* Batch Delete Confirmation Modal */}
      {showBatchDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Confirm Batch Delete
              </h3>
              <button
                onClick={() => setShowBatchDeleteConfirm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700">
                Are you sure you want to delete {selectedUserIds.length}{" "}
                selected user(s)?
              </p>
              <p className="text-red-600 text-sm mt-2">
                This action cannot be undone and will permanently delete all
                selected users and their associated data.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBatchDeleteConfirm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                disabled={batchDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleBatchDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={batchDeleting}
              >
                {batchDeleting ? "Deleting..." : "Delete Users"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
